import type {
  DateValidationError,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers';
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  FocusEvent,
  ReactNode,
} from 'react';
import { forwardRef, useState } from 'react';

type DateFieldProps = ComponentPropsWithoutRef<typeof DatePicker> & {
  required?: boolean;
  fullWidth?: boolean;
  helperText?: ReactNode;
  error?: boolean;
  onBlur: (event: FocusEvent) => void;
  onDateChanged: (date: Date) => void;
};
type DateFieldRef = ComponentRef<typeof DatePicker>;
const DateField = forwardRef<DateFieldRef, DateFieldProps>((props, ref) => {
  const [date, setDate] = useState<Date | null>(null);
  const {
    required,
    fullWidth,
    helperText,
    error,
    slotProps,
    onBlur,
    onDateChanged,
    ...rest
  } = props;

  const onAccept = (
    date: Date | null,
    context: PickerChangeHandlerContext<DateValidationError>,
  ) => {
    if (context.source === 'view' && date && onDateChanged) onDateChanged(date);
    setDate(date);
  };
  const handleFieldBlur = (event: FocusEvent) => {
    if (onBlur) onBlur(event);
    if (date) onDateChanged(date);
  };

  return (
    <DatePicker
      ref={ref}
      onAccept={onAccept}
      slotProps={{
        textField: {
          required,
          fullWidth,
          helperText,
          error,
          onBlur: handleFieldBlur,
        },
        ...slotProps,
      }}
      {...rest}
    />
  );
});
DateField.displayName = 'DateField';
export default DateField;

// Option 1: Use DesktopDatePicker with a plain TextField slot (recommended)
// This removes the mask, keeps parsing/validation, and supports copy/paste.
// import * as React from "react";
// import dayjs, { Dayjs } from "dayjs";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
// import TextField from "@mui/material/TextField";
//
// export default function FreeTypingDate() {
//     const [value, setValue] = React.useState<Dayjs | null>(null);
//     const [raw, setRaw] = React.useState<string>(""); // keep raw text for UX
//
//     return (
//         <LocalizationProvider dateAdapter={AdapterDayjs}>
//             <DesktopDatePicker
//                 value={value}
//                 onChange={(newValue) => setValue(newValue)}
//                 format="DD/MM/YYYY"         // choose your display/parse format
//                 disableMaskedInput          // v5: removes mask in some editions
//                 slotProps={{
//                     textField: {
//                         value: raw,         // control raw input text
//                         onChange: (e) => {
//                             const next = e.target.value;
//                             setRaw(next);
//                             const parsed = dayjs(next, "DD/MM/YYYY", true);
//                             if (parsed.isValid()) {
//                                 setValue(parsed);
//                             } else {
//                                 setValue(null);
//                             }
//                         },
//                         onBlur: () => {
//                             // normalize text on blur if valid
//                             if (value && value.isValid()) {
//                                 setRaw(value.format("DD/MM/YYYY"));
//                             }
//                         },
//                         // Validation feedback for user
//                         error: raw.length > 0 && !value,
//                         helperText:
//                             raw.length > 0 && !value
//                                 ? "Use format DD/MM/YYYY"
//                                 : " ",
//                         placeholder: "DD/MM/YYYY",
//                         inputProps: {
//                             // Allow full free typing and paste
//                             inputMode: "text",
//                             // Optional: turn off autocomplete
//                             autoComplete: "off",
//                         },
//                     },
//                 }}
//             />
//         </LocalizationProvider>
//     );
// }

// import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
// import IconButton from '@mui/material/IconButton';
// import Popover from '@mui/material/Popover';
// import TextField from '@mui/material/TextField';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import dayjs, { Dayjs } from 'dayjs';
//
// XXX: consider using simple text field with validation and calendar widget
// function BaseDateField() {
//   const [anchorEl, setAnchorEl] = React.useState < HTMLElement | null > (null);
//   const [raw, setRaw] = React.useState('');
//   const [value, setValue] = React.useState < Dayjs | null > (null);
//
//   const open = Boolean(anchorEl);
//
//   return (
//     <LocalizationProvider dateAdapter={AdapterDayjs}>
//       <TextField
//         value={raw}
//         helperText={
//           raw.length > 0 && !value ? 'Use format DD/MM/YYYY' : ' '
//         }
//         placeholder="DD/MM/YYYY"
//         error={raw.length > 0 && !value}
//         onBlur={() => {
//           if (value) {
//             setRaw(value.format('DD/MM/YYYY'));
//           }
//         }}
//         onChange={(e) => {
//           const next = e.target.value;
//           setRaw(next);
//           const parsed = dayjs(next, 'DD/MM/YYYY', true);
//           setValue(parsed.isValid() ? parsed : null);
//         }}
//         InputProps={{
//           endAdornment: (
//             <IconButton
//               size="small"
//               aria-label="Open calendar"
//               onClick={(e) => setAnchorEl(e.currentTarget)}
//             >
//               <CalendarTodayIcon fontSize="small" />
//             </IconButton>
//           ),
//         }}
//       />
//
//       <Popover
//         open={open}
//         anchorEl={anchorEl}
//         onClose={() => setAnchorEl(null)}
//         anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
//       >
//         <DateCalendar
//           value={value}
//           onChange={(newVal) => {
//             setValue(newVal);
//             setRaw(newVal ? newVal.format('DD/MM/YYYY') : '');
//             setAnchorEl(null);
//           }}
//         />
//       </Popover>
//     </LocalizationProvider>
//   );
// }
